import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Typography, Table, Input, Space, Dropdown, Button, App, Tooltip, Image, Avatar, Select, Modal, Form, Alert } from 'antd';
import { MoreOutlined, CopyOutlined, EditOutlined, DeleteOutlined, PlusOutlined, PictureOutlined, FileExcelOutlined, StopOutlined } from '@ant-design/icons';
import { resolveMedia } from '../../utils/media';
import { cloneCompetition, listCompetitions, deleteCompetition, cancelCompetition } from '../../services/competitions';
import { exportToExcel } from '../../utils/excel';
import moment from 'moment';
import { listGenders, listModalities, listPeriods } from '../../services/catalogs';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import usePermissions from '../../hooks/usePermissions';

const { Title } = Typography;
const { useApp } = App;

const CompetitionList = () => {
  const navigate = useNavigate();
  const { message, modal } = useApp();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const { can } = usePermissions();

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [sorter, setSorter] = useState({});
  const [search, setSearch] = useState('');
  const [modalityFilter, setModalityFilter] = useState(null);
  const [periodFilter, setPeriodFilter] = useState(null);
  const [genderFilter, setGenderFilter] = useState(null);
  const [startDate, setStartDateFilter] = useState(null);
  const [endDate, setEndDateFilter] = useState(null);

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelRecord, setCancelRecord] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelForm] = Form.useForm();

  // Catálogos para los filtros
  const [periods, setPeriods] = useState([]);
  const [genders, setGenders] = useState([]);
  const [modalities, setModalities] = useState([]);

  useEffect(() => {
    const loadCatalogs = async () => {
          try {
            const [
              periodsData,
              gendersData,
              modalitiesData,
            ] = await Promise.all([
              listPeriods(),
              listGenders(),
              listModalities(),
            ]);
            setPeriods(periodsData || []);
            setGenders(gendersData || []);
            setModalities(modalitiesData || []);
          } catch (error) {
            console.error('Error al cargar catálogos:', error);
            message.error('No se pudieron cargar los catálogos');
          }
        };
    loadCatalogs();
  }, [message]);

  const fetchData = useCallback(async (page, pageSize, srt, q, modalityId, peridoId, genderId, startDate, endDate) => {
    setLoading(true);
    try {
      const params = {
        search: q || undefined,
        page: page || pagination.current,
        pageSize: pageSize || pagination.pageSize,
        sortField: srt?.field,
        sortOrder: srt?.order,
        ...(modalityId && { modalityId: modalityId }),
        ...(peridoId && { periodId: peridoId }),
        ...(genderId && { genderId: genderId }),
        ...(startDate && { startDate: startDate }),
        ...(endDate && { endDate: endDate }),
      };

      const { items, total } = await listCompetitions(params);
      setData(items || []);
      setPagination(prev => ({
        ...prev,
        current: page || prev.current,
        pageSize: pageSize || prev.pageSize,
        total: total || 0
      }));
    } catch (err) {
      console.error(err);
      message.error('No se pudo cargar la lista de competencias.');
    } finally {
      setLoading(false);
    }
  }, [pagination, message]);

  useEffect(() => {
    fetchData(1, pagination.pageSize, null, search, modalityFilter, periodFilter, genderFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (pag, filters, srt) => {
    setSorter(srt || {});
    fetchData(pag.current, pag.pageSize, srt, search, modalityFilter, periodFilter, genderFilter, startDate, endDate);
  };

  const onSearch = (value) => {
    setSearch(value || '');
    fetchData(1, pagination.pageSize, sorter, value || '', modalityFilter, periodFilter, genderFilter, startDate, endDate);
  };

  const handleModalityFilter = (value) => {
    setModalityFilter(value);
    fetchData(1, pagination.pageSize, sorter, search, value, periodFilter, genderFilter, startDate, endDate);
  };

  const handlePeriodFilter = (value) => {
    setPeriodFilter(value);
    fetchData(1, pagination.pageSize, sorter, search, modalityFilter, value, genderFilter, startDate, endDate);
  };

  const handleGenderFilter = (value) => {
    setGenderFilter(value);
    fetchData(1, pagination.pageSize, sorter, search, modalityFilter, periodFilter, value, startDate, endDate);
  };

  const handleStartDateFilter = (date, dateString) => {
    setStartDateFilter(dateString);
    fetchData(1, pagination.pageSize, sorter, search, modalityFilter, periodFilter, genderFilter, dateString, endDate);
  };

  const handleEndDateFilter = (date, dateString) => {
    setEndDateFilter(dateString);
    fetchData(1, pagination.pageSize, sorter, search, modalityFilter, periodFilter, genderFilter, startDate, dateString);
  };

  const isCancellable = (record) => {
    const statusId = record.status?.id;
    return [2, 3].includes(statusId);
  };

  const handleOpenCancelModal = (record) => {
    setCancelRecord(record);
    setCancelModalOpen(true);
  };

  const handleConfirmCancel = async () => {
    try {
      const values = await cancelForm.validateFields();
      setCancelLoading(true);
      await cancelCompetition(cancelRecord.id, {
        cancellationReason: values.cancellationReason,
      });
      message.success('Competencia cancelada correctamente');
      setCancelModalOpen(false);
      cancelForm.resetFields();
      setCancelRecord(null);
      fetchData(pagination.current, pagination.pageSize, sorter, search, modalityFilter, periodFilter, genderFilter, startDate, endDate);
    } catch (err) {
      if (err?.errorFields) return;
      message.error('No se pudo cancelar la competencia');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleCloseCancelModal = () => {
    setCancelModalOpen(false);
    cancelForm.resetFields();
    setCancelRecord(null);
  };

  const handleMenuAction = (action, record) => {
    switch (action) {
      case 'clone':
        modal.confirm({
          title: '¿Clonar competencia?',
          content: `Se creará una copia de "${record.description || 'competencia'}".`,
          okText: 'Clonar',
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              await cloneCompetition(record.id);
              message.success('Competencia clonada correctamente');
              fetchData(
                pagination.current,
                pagination.pageSize,
                sorter,
                search,
                modalityFilter,
                periodFilter,
                genderFilter
              );
            } catch {
              message.error('No se pudo clonar la competencia');
            }
          },
        });
        break;
      case 'cancel':
        handleOpenCancelModal(record);
        break;
      case 'configure':
        navigate(`/competitions/${record.id}/configure`);
        break;
      case 'edit':
        navigate(`/competitions/${record.id}/edit`);
        break;
      case 'delete':
        modal.confirm({
          title: '¿Eliminar competencia?',
          content: `${record.title || 'competencia'}`,
          okText: 'Eliminar',
          okButtonProps: { danger: true },
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              if(['aprobada', 'Aprobada', 'En Configuración'].includes( record.status?.description)){
                await deleteCompetition(record.id);
                message.success('competencia eliminada');

                const nextPage = (data.length === 1 && pagination.current > 1)
                  ? pagination.current - 1
                  : pagination.current;
                setPagination(prev => ({ ...prev, current: nextPage }));
                fetchData(nextPage, pagination.pageSize, sorter, search, modalityFilter, periodFilter, genderFilter);
              }
            } catch {
              message.error('No se pudo eliminar la competencia');
            }
          },
        });
        break;
      default:
        break;
    }
  };

  const getActionsMenu = (record) => {
    const menuItems = [
      can('competitions.clone') && {
        key: 'clone',
        icon: <CopyOutlined />,
        label: 'Clonar',
      },
      can('competitions.edit') && {
        key: 'edit',
        icon: <EditOutlined />,
        label: 'Editar',
      },
      {
        type: 'divider',
      },
      ...(isCancellable(record)
        ? [
          can('competitions.cancel') && {
            key: 'cancel',
            icon: <StopOutlined />,
            label: 'Cancelar competencia',
            danger: true,
          }
        ] : []
      ),
      ...(['aprobada', 'Aprobada', 'En Configuración'].includes( record.status?.description)
        ? [
          can('competitions.delete') && {
            key: 'delete',
            icon: <DeleteOutlined />,
            label: 'Eliminar',
            danger: true,
          }
       ] : []
      ),
    ];

    return {
      items: menuItems,
      onClick: ({ key }) => handleMenuAction(key, record),
    };
  };

  const columns = [
    {
      title: 'Imagen',
      dataIndex: 'urlImage',
      key: 'image',
      width: 96,
      align: 'center',
      render: (_, r) => {
        const mediaPath = r.urlImage || r.media_path;
        const src = resolveMedia(mediaPath);
        const label = r.title || 'competencia';
        if (!src) {
          return <Avatar shape="square" size={64} icon={<PictureOutlined />} />;
        }
        return (
          <Image
            src={src}
            alt={label}
            width={64}
            height={64}
            style={{ objectFit: 'cover', borderRadius: 8 }}
            onClick={(e) => e.stopPropagation()}
            preview={{ mask: 'Ver' }}
            fallback="data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='64' height='64'/>"
          />
        );
      },
    },
    {
      title: 'competencia',
      dataIndex: 'description',
      key: 'description',
      render: (text) => text || '-',
      sorter: true,
    },
    {
      title: 'Modalidad',
      dataIndex: ['modality', 'description'],
      key: 'modality',
      render: (_, r) => r.modality?.description || r.modality?.code || '-',
      sorter: true,
    },
    {
      title: 'Periodo',
      dataIndex: ['period', 'description'],
      key: 'period',
      render: (_, r) => r.period?.description || r.period?.code || '-',
      sorter: true,
    },
    {
      title: 'Genero',
      dataIndex: ['gender', 'description'],
      key: 'gender',
      render: (_, r) => r.gender?.description || r.gender?.code || '-',
      sorter: true,
    },
    {
      title: 'Fecha Inicio',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (text) => text? moment(text).format('DD/MM/YYYY') : '-',
      sorter: true,
    },
    {
      title: 'Hora Inicio',
      dataIndex: 'startHour',
      key: 'startHour',
      render: (text) =>text ? moment(text, 'HH:mm').format('hh:mm A') : '-',
      sorter: true,
    },
    {
      title: 'Fecha Fin',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (text) => text ? moment(text).format('DD/MM/YYYY') : '-',
      sorter: true,
    },
    {
      title: 'Hora Fin',
      dataIndex: 'endHour',
      key: 'endHour',
      render: (text) => text ? moment(text, 'HH:mm').format('hh:mm A') : '-',
      sorter: true,
    },
    {
      title: 'Estatus',
      dataIndex: ['status', 'description'],
      key: 'status',
      render: (_, r) => r.status?.description || r.status?.code || '-',
    },
    {
      title: 'Motivo cancelación',
      dataIndex: 'cancellationReason',
      key: 'cancellationReason',
      width: 120,
      render: (text) => {
        if (!text) return '-';
        return (
          <Tooltip title={text} placement="topLeft">
            <span style={{
              display: 'block',
              maxWidth: 120,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              cursor: 'default',
            }}>
              {text}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      width: 100,
      render: (_, record) => (
        <Dropdown menu={getActionsMenu(record)} trigger={['click']} placement="bottomRight">
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
            style={{ fontSize: '16px' }}
          />
        </Dropdown>
      ),
    },
  ];

  const handleExportToExcel = async () => {
    setExporting(true);
    try {
      const { items } = await listCompetitions({
        search: search || undefined,
        page: 1,
        pageSize: 500, 
        sortField: sorter?.field,
        sortOrder: sorter?.order,
      });

      if (!items || items.length === 0) {
        message.warning('No hay competencias para exportar');
        return;
      }

      // Preparar los datos para Excel
      const excelData = items.map((competition, index) => {

        return {
          '#': index + 1,
          'Competencia': competition.description || '-',
          'Modalidad': competition.modality?.description || '-',
          'Periodo': competition.period?.description || '-',
          'Fecha Inicio': competition.startDate 
            ? moment(competition.startDate).format('DD/MM/YYYY') 
            : '-',
          'Hora Inicio': competition.startHour ? moment(competition.startHour, 'HH:mm').format('hh:mm A') : '-',
          'Fecha Fin': competition.endDate 
            ? moment(competition.endDate).format('DD/MM/YYYY') 
            : '-',
          'Hora Fin': competition.endHour ? moment(competition.endHour, 'HH:mm').format('hh:mm A') : '-',
          'Estatus': competition.status?.description || '-',
          'Motivo de cancelación': competition.cancellationReason || '-',
        };
      });

      const columnWidths = [
        { wch: 5 },
        { wch: 30 },
        { wch: 50 },
        { wch: 50 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 50 },
        { wch: 20 },
      ];

      exportToExcel(excelData, columnWidths, 'Competencias', 'competencias');

    } catch (err) {
      console.error('Error al exportar a Excel:', err);
      message.error('No se pudo exportar las competencias a Excel');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <div className="header-container">
              <Title level={3} style={{ marginBottom: 0 }}>Competencias</Title>
              <Space>
                {can('competitions.export') && (<Tooltip title="Exportar a Excel">
                    <Button
                      className='btn-add'
                      icon={<FileExcelOutlined />}
                      onClick={handleExportToExcel}
                      loading={exporting}
                      disabled={exporting}
                      aria-label="Exportar a Excel"
                    />
                </Tooltip>)}
                {can('competitions.add') && (<Tooltip title="Crear competencia">
                  <Button
                    type="primary"
                    className='btn-add'
                    icon={<PlusOutlined />}
                    aria-label="Crear competencia"
                    onClick={() => navigate('/competitions/add')}
                  />
                </Tooltip>)}
              </Space>
            </div>
      <Card
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space wrap>
            <Input.Search
              placeholder="Buscar"
              allowClear
              onSearch={onSearch}
              style={{ width: 200 }}
            />
            <Select
              placeholder="Modalidad"
              allowClear
              style={{ width: 150 }}
              value={modalityFilter}
              onChange={handleModalityFilter}
              options={modalities.map(modality => ({
                value: modality.id,
                label: modality.description || modality.code,
              }))}
            />
            <Select
              placeholder="Periodo"
              allowClear
              style={{ width: 150 }}
              value={periodFilter}
              onChange={handlePeriodFilter}
              options={periods.map(period => ({
                value: period.id,
                label: period.description || period.code,
              }))}
            />
            <Select
              placeholder="Genero"
              allowClear
              style={{ width: 150 }}
              value={genderFilter}
              onChange={handleGenderFilter}
              options={genders.map(gender => ({
                value: gender.id,
                label: gender.description || gender.code,
              }))}
            />
            <DatePicker
              placeholder="Fecha de inicio"
              style={{ width: 150 }}
              value={startDate ? dayjs(startDate, 'YYYY-MM-DD') : null}
              onChange={handleStartDateFilter}
            />
            <DatePicker
              placeholder="Fecha de fin"
              style={{ width: 150 }}
              value={endDate ? dayjs(endDate, 'YYYY-MM-DD') : null}
              onChange={handleEndDateFilter}
            />
          </Space>
          <Table
            rowKey={(r) => r.id ?? r._id}
            columns={columns}
            dataSource={data}
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
              showTotal: (total) => `Total: ${total} competencias`
            }}
            onChange={onChange}
          />
        </Space>
      </Card>

      <Modal
        title="Cancelar competencia"
        open={cancelModalOpen}
        onOk={handleConfirmCancel}
        onCancel={handleCloseCancelModal}
        okText="Confirmar cancelación"
        cancelText="Volver"
        okButtonProps={{ danger: true, loading: cancelLoading }}
        destroyOnClose
      >
        <Alert
          message="¿Estás seguro que deseas cancelar esta competencia? Esta acción no se puede revertir."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form form={cancelForm} layout="vertical">
          <Form.Item
            name="cancellationReason"
            label="Motivo de cancelación"
            rules={[{ required: true, message: 'El motivo de cancelación es requerido' }]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Ingrese el motivo de cancelación..."
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CompetitionList;

